import {
  Datagrid,
  Filter,
  FunctionField,
  PaginationLimit,
  ReferenceField,
  ResourceContextProvider,
  SearchInput,
  TextField,
} from "react-admin";
import StatusField from "../../../components/StatusField";
import CustomPriceField from "../../../components/CustomPriceField";
import EmptyComponent from "./EmptyComponent";
import CustomDateField from "../../../components/CustomDateField";
import React, { useEffect, useState } from "react";
import BulkConfirmButton from "./BulkConfirmButton";
import SubcontractorPurchaseInvoicePanel from "./SubcontractorPurchaseInvoicePanel";
import { availableOnlySuperAdmin } from "utils/validation";
import ApproveButton from "./ApproveButton";
import RejectButton from "./RejectButton";
import ShowPdfButton from "./ShowPdfButton";
import RegeneratePdfButton from "./RegeneratePdfButton";
import ActionsMenu from "../../../components/ActionsMenu";
import { PaddingDenseStyled2List } from "../../../components/ListWrapper";
import DownloadExcelButton from "./DownloadExcelButton";
import UpdatedByField from "components/UpdatedByField";
import RegeneratePdfForSubcontractorButton from "./RegeneratePdfForSubcontractorButton";
import ShowPdfForSubcontractorButton from "./ShowPdfForSubcontractorButton";
import CustomPagination from "../../../components/CustomPagination";

interface IPurchaseInvoiceTable {
  isFixedHeight?: boolean;
  purchaseInvoiceSummaryId?: number;
  subcontractorId?: number;
  statusIn?: string[];
}

const SubcontractorPurchaseInvoiceTableFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />
  </Filter>
);

const SubcontractorPurchaseInvoiceTable = ({
  isFixedHeight,
  purchaseInvoiceSummaryId,
  subcontractorId,
  statusIn,
}: IPurchaseInvoiceTable) => {
  const isDraft = statusIn.includes("Draft");
  const [refreshTable, setRefreshTable] = useState<boolean>(false);

  useEffect(() => {
    if (refreshTable) {
      // It will change the key twice (refresh-false => refresh-true => refresh-false)
      // to force re render the table
      setRefreshTable(false);
    }
  }, [refreshTable]);

  return (
    <ResourceContextProvider value="subcontractorPurchaseInvoices">
      <PaddingDenseStyled2List
        key={refreshTable ? "refresh-true" : "refresh-false"} // Use key to force re-render
        basePath="/subcontractorPurchaseInvoices"
        actions={null}
        exporter={false}
        hasCreate={false}
        filters={<SubcontractorPurchaseInvoiceTableFilter />}
        filterDefaultValues={{
          StatusIn: statusIn,
          SubcontractorPurchaseInvoiceSummaryId: purchaseInvoiceSummaryId,
          SubcontractorId: subcontractorId,
        }}
        sort={{ field: "id", order: "DESC" }}
        bulkActionButtons={
          isDraft &&
          availableOnlySuperAdmin() && <BulkConfirmButton isDraft={isDraft} />
        }
        title=" "
        pagination={
          <CustomPagination
            limit={isFixedHeight ? <EmptyComponent /> : <PaginationLimit />}
          />
        }
      >
        <Datagrid expand={<SubcontractorPurchaseInvoicePanel />}>
          <TextField source="id" emptyText="-" />
          <ReferenceField
            source="subcontractorId"
            label="Subcontractor Identifier"
            reference="subcontractors"
            link={false}
          >
            <TextField source="subcontractorIdentifier" emptyText="-" />
          </ReferenceField>
          <ReferenceField
            source="subcontractorId"
            label="Subcontractor MBC Code"
            reference="subcontractors"
            link={false}
          >
            <TextField source="mbcVendorId" emptyText="-" />
          </ReferenceField>
          <ReferenceField
            source="subcontractorId"
            label="Subcontractor"
            reference="subcontractors"
            link={false}
          >
            <TextField source="name" />
          </ReferenceField>
          <TextField source="mbcRef" emptyText="-" />
          <StatusField source="status" />
          <TextField source="description" emptyText="-" />
          <CustomDateField source="dueAt" />
          <CustomPriceField source="subTotal" />
          <CustomPriceField source="tax" />
          <CustomPriceField source="totalDue" />
          {!isDraft && (
            <FunctionField
              label="Updated By"
              render={(record: any) => (
                <>
                  {record.updatedBy === null ? (
                    "-"
                  ) : (
                    <UpdatedByField data={record.updatedBy} />
                  )}
                </>
              )}
            />
          )}

          <FunctionField
            label="Actions"
            render={(record: any) => (
              <ActionsMenu
                numberOfActions={7}
                buttonDirection={"horizontal"}
                render={[
                  isDraft && availableOnlySuperAdmin() && (
                    <ApproveButton id={record.id} />
                  ),
                  isDraft && availableOnlySuperAdmin() && (
                    <RejectButton id={record.id} />
                  ),
                  <DownloadExcelButton id={record.id} />,
                  record.pdfUrl && <ShowPdfButton id={record.id} />,
                  <RegeneratePdfButton
                    id={record.id}
                    setRefresh={setRefreshTable}
                  />,
                  record.pdfUrlForSubcontractor && (
                    <ShowPdfForSubcontractorButton id={record.id} />
                  ),
                  <RegeneratePdfForSubcontractorButton
                    id={record.id}
                    setRefresh={setRefreshTable}
                  />,
                ]}
              />
            )}
          />
        </Datagrid>
      </PaddingDenseStyled2List>
    </ResourceContextProvider>
  );
};

export default SubcontractorPurchaseInvoiceTable;
